import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Route, Send, ShieldQuestion, AlertTriangle, CheckCircle2, HelpCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VERDICT = {
  pass: { cls: "bg-teal-light text-teal", Icon: CheckCircle2 },
  likely: { cls: "bg-lime/25 text-navy", Icon: CheckCircle2 },
  stripped: { cls: "bg-red-50 text-red-700", Icon: AlertTriangle },
  inconclusive: { cls: "bg-amber-50 text-amber-700", Icon: HelpCircle },
  unreachable: { cls: "bg-red-50 text-red-700", Icon: AlertTriangle },
};

export const JourneyPanel = ({ authHeader }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/admin/bdm-journey`, { headers: authHeader() });
      setData(d);
    } catch {
      setData({ settings: { enabled: false }, partners: [], steps: [] });
    }
  }, [authHeader]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (enabled) => {
    setBusy(true);
    try {
      const { data: s } = await axios.put(`${API}/admin/bdm-journey`, { enabled }, { headers: authHeader() });
      setData((d) => ({ ...d, settings: s }));
      toast.success(enabled ? "Activation journey is on" : "Activation journey paused");
    } catch {
      toast.error("Couldn't change that.");
    } finally {
      setBusy(false);
    }
  };

  const runNow = async () => {
    setBusy(true);
    try {
      const { data: res } = await axios.post(`${API}/admin/bdm-journey/run`, {}, { headers: authHeader() });
      const n = (res.sent || []).length;
      toast.success(n ? `${n} prompt${n === 1 ? "" : "s"} sent` : "Nothing was due right now");
      await load();
    } catch {
      toast.error("Couldn't send those.");
    } finally {
      setBusy(false);
    }
  };

  if (!data) {
    return (
      <div className="rounded-2xl bg-white border border-linen-border shadow-soft p-6 flex items-center gap-2 text-sm text-ink-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading the journey…
      </div>
    );
  }

  const stalled = (data.partners || []).filter((p) => p.smaller_sent > 0 && !p.finished);

  return (
    <div className="rounded-2xl bg-white border border-linen-border shadow-soft p-6" data-testid="journey-panel">
      <div className="flex items-center gap-2.5">
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-teal-light text-teal">
          <Route size={17} />
        </span>
        <div>
          <h3 className="font-heading font-medium text-lg text-navy">Activation journey</h3>
          <p className="text-xs text-ink-500">
            {data.steps.length} steps over the first 30 days. Nobody gets the next step until the
            current one is done — they get a smaller version of it instead.
          </p>
        </div>
      </div>

      <div className="mt-5 rounded-2xl bg-linen/60 border border-linen-border p-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={Boolean(data.settings.enabled)}
            onChange={(e) => toggle(e.target.checked)}
            disabled={busy}
            data-testid="journey-enabled"
            className="mt-0.5 w-4 h-4 accent-teal"
          />
          <span>
            <span className="text-sm font-semibold text-navy">Send the daily prompts automatically</span>
            <span className="block text-[12px] text-ink-500 leading-relaxed">
              One email per partner per day at most, and only when their next step is actually due.
              Completion is read from what they tick on their dashboard — we never ask.
            </span>
          </span>
        </label>
        <div className="mt-3 flex flex-wrap items-center gap-3 pl-7">
          <button
            onClick={runNow}
            disabled={busy}
            data-testid="journey-run-now"
            className="inline-flex items-center gap-1.5 bg-white border border-linen-border hover:border-teal text-navy font-semibold text-xs px-4 py-2 rounded-full transition-colors disabled:opacity-60"
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Send what's due
          </button>
          {data.settings.last_sent_count ? (
            <span className="text-[11px] text-ink-400">Last sweep sent {data.settings.last_sent_count}</span>
          ) : null}
          {stalled.length > 0 && (
            <span className="text-[11px] text-amber-700 font-semibold">
              {stalled.length} stalled on a step
            </span>
          )}
        </div>
      </div>

      {data.partners.length > 0 && (
        <div className="mt-5 overflow-x-auto">
          <table className="w-full text-sm" data-testid="journey-table">
            <thead>
              <tr className="text-left text-ink-500 border-b border-linen-border">
                <th className="py-2 pr-3 font-medium">Partner</th>
                <th className="py-2 pr-3 font-medium">Step</th>
                <th className="py-2 pr-3 font-medium">On</th>
                <th className="py-2 font-medium">State</th>
              </tr>
            </thead>
            <tbody>
              {data.partners.map((p) => (
                <tr key={p.id} className="border-b border-linen-border/60" data-testid={`journey-row-${p.id}`}>
                  <td className="py-2.5 pr-3 font-medium text-navy">{p.name}</td>
                  <td className="py-2.5 pr-3 tabular-nums text-ink-600">{p.step}/{p.total}</td>
                  <td className="py-2.5 pr-3 text-ink-500 text-xs">{p.title}</td>
                  <td className="py-2.5">
                    {p.finished ? (
                      <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-md bg-teal-light text-teal">
                        Graduated
                      </span>
                    ) : p.smaller_sent > 0 ? (
                      <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-200">
                        Shrunk ×{p.smaller_sent}
                      </span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-md bg-linen text-ink-600">
                        On track
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export const AttributionProbeCard = ({ authHeader }) => {
  const [state, setState] = useState(null);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/admin/attribution-probe`, { headers: authHeader() })
      .then(({ data }) => {
        setState(data);
        setUrl(data.last_result?.target_url || data.suggested_url || "");
      })
      .catch(() => setState({ last_result: null, suggested_url: "" }));
  }, [authHeader]);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/attribution-probe`, { url }, { headers: authHeader() });
      setState((s) => ({ ...s, last_result: data }));
      toast.success("Probe finished");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't run that probe.");
    } finally {
      setBusy(false);
    }
  };

  if (!state) {
    return (
      <div className="rounded-2xl bg-white border border-linen-border shadow-soft p-6 flex items-center gap-2 text-sm text-ink-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading the probe…
      </div>
    );
  }

  const r = state.last_result;
  const tone = VERDICT[r?.verdict] || VERDICT.inconclusive;
  const Icon = tone.Icon;

  return (
    <div className="rounded-2xl bg-white border border-linen-border shadow-soft p-6" data-testid="attribution-probe-card">
      <div className="flex items-center gap-2.5">
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-teal-light text-teal">
          <ShieldQuestion size={17} />
        </span>
        <div>
          <h3 className="font-heading font-medium text-lg text-navy">Attribution check</h3>
          <p className="text-xs text-ink-500">
            Does a referral tag survive into the application? Answer this before building tracking on it.
          </p>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2.5">
        <label className="block">
          <span className="text-xs uppercase tracking-widest text-ink-500">Application URL to test</span>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://…"
            data-testid="probe-url"
            className="mt-1 w-full rounded-xl border border-linen-border p-2.5 text-sm text-navy bg-linen/40 focus:bg-white focus:border-teal focus:outline-none"
          />
          {state.suggested_from && (
            <span className="mt-1 block text-[11px] text-ink-400">
              Pre-filled from /{state.suggested_from}'s application link.
            </span>
          )}
        </label>
        <button
          onClick={run}
          disabled={busy || !url.trim()}
          data-testid="probe-run"
          className="sm:self-start sm:mt-6 inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy-light text-white font-semibold text-sm px-5 py-2.5 rounded-full transition-colors disabled:opacity-50"
        >
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Run the check
        </button>
      </div>

      {r && (
        <div className="mt-5 rounded-2xl border border-linen-border p-5" data-testid="probe-result">
          <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] uppercase tracking-widest font-bold ${tone.cls}`}>
            <Icon size={13} /> {r.verdict}
          </span>
          <h4 className="mt-3 font-heading font-medium text-lg text-navy">{r.headline}</h4>
          <p className="mt-2 text-sm text-ink-600 leading-relaxed">{r.next_step}</p>

          <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-[12.5px]">
            {[
              ["Tag kept in the final URL", r.tag_in_final_url],
              ["Tag echoed in the page", r.tag_echoed_in_page],
              ["Page has a matching field", r.has_named_field],
              ["Browser-rendered app", r.looks_like_spa],
            ].map(([label, value]) => (
              <div key={label} className="flex items-center justify-between gap-2 border-b border-linen-border/60 py-1">
                <dt className="text-ink-500">{label}</dt>
                <dd className={value ? "text-teal font-semibold" : "text-ink-400"}>{value ? "Yes" : "No"}</dd>
              </div>
            ))}
          </dl>

          {r.redirect_hops?.length > 0 && (
            <p className="mt-3 text-[11px] text-ink-400 break-all">
              Redirected {r.redirect_hops.length}× · ended at {r.final_url}
            </p>
          )}
          <p className="mt-3 text-[11px] text-ink-400 break-all">Probed: {r.probe_url}</p>
        </div>
      )}
    </div>
  );
};

export default JourneyPanel;
